<ng-container *ngIf="loadState === 'ready'; else otherStates">
  <ng-container *ngIf="claim$ | async as claim">
    <vs-app-page
      [config]="{ icon: 'layout-left', title: config.title(claim.claimNumber), description: config.description }"
    >
      <vs-badge slot="title-end" [theme]="claimTheme(claim.status)" [config]="{ displayStatusIcon: true }">
        {{ claim.status.replace('CANCELLED', 'CANCELED') | titlecase }}
      </vs-badge>

      <vs-breadcrumbs slot="breadcrumbs">
        <vs-breadcrumbs-item
          [config]="{ label: config.breadcrumbs.claims, routerLink: '/claims' }"
        ></vs-breadcrumbs-item>
        <vs-breadcrumbs-item
          [config]="{ label: config.title(claim.claimNumber), routerLink: '/claims/' + claim.id }"
        ></vs-breadcrumbs-item>
        <vs-breadcrumbs-item [config]="{ label: config.breadcrumbs.workspace }"></vs-breadcrumbs-item>
      </vs-breadcrumbs>

      <div slot="header-end" class="flex gap-2">
        <!-- Same rule as the claim page: once every job is resolved or rejected -->
        <button
          *ngIf="(permissions$ | async)?.closeClaim && claim.status === open && hasOnlyFinishedJobs(claim)"
          vs-button
          appearance="outline"
          data-testid="workspace-close"
          (click)="closeDialog.open(claim)"
        >
          {{ closeConfig.open }}
        </button>
        <a vs-button appearance="outline" [routerLink]="'/claims/' + claim.id">
          <vs-icon name="file-06"></vs-icon> {{ config.classicView }}
        </a>
      </div>

      <div class="grid gap-4 lg:grid-cols-[300px_minmax(0,1fr)] items-start">
        <!-- Claim context stays put while you pick jobs -->
        <aside class="lg:sticky lg:top-4 flex flex-col gap-3" data-testid="workspace-context">
          <vs-card class="p-4 flex flex-col gap-4">
            <p class="text-sm font-semibold text-gray-900">{{ config.context.title }}</p>

            <div data-testid="context-contract">
              <p class="text-xs font-medium text-gray-500">{{ config.context.contract }}</p>
              <div class="flex items-center gap-2">
                <p class="text-sm font-medium text-gray-900 truncate">{{ claim.warranty?.contractNumber }}</p>
                <vs-badge *ngIf="claim.warranty" [theme]="warrantyTheme(claim.warranty)">
                  {{ claim.warranty.status | titlecase }}
                </vs-badge>
              </div>
              <p class="text-xs text-gray-500 truncate">
                {{ claim.warranty?.policy?.title }} · {{ claim.warranty?.plan?.title }}
              </p>
            </div>

            <div data-testid="context-customer">
              <p class="text-xs font-medium text-gray-500">{{ config.context.customer }}</p>
              <p class="text-sm text-gray-900 truncate">{{ claim.warranty?.customer?.name }}</p>
              <p class="text-xs text-gray-500 truncate">{{ claim.warranty?.customer?.email }}</p>
            </div>

            <!-- Labels come from the asset's property set -->
            <div data-testid="context-asset">
              <p class="text-xs font-medium text-gray-500">{{ config.context.asset }}</p>
              <ng-container *ngIf="claim.warranty?.asset as asset; else noAsset">
                <p class="text-sm text-gray-900 truncate mb-1">{{ asset.name }}</p>
                <vs-property-set-values
                  [propertySetId]="claim.propertySetId ?? asset.propertySetId"
                  [propertyValues]="(claim.propertySetSubmission ?? claim.warranty?.propertySetSubmission)?.value || {}"
                  [dimensionValues]="
                    (claim.propertySetSubmission ?? claim.warranty?.propertySetSubmission)?.dimensionValues || {}
                  "
                  [showHidden]="true"
                ></vs-property-set-values>
              </ng-container>
              <ng-template #noAsset>
                <p class="text-sm italic text-gray-500">{{ config.context.noAsset }}</p>
              </ng-template>
            </div>

            <div data-testid="context-coverage">
              <p class="text-xs font-medium text-gray-500">{{ config.context.coverage }}</p>
              <ng-container *ngIf="selectedJob$ | async as job; else noJob">
                <p class="text-sm text-gray-900">{{ job.coverage?.title ?? '-' }}</p>
                <dl class="text-xs text-gray-600 flex flex-col gap-0.5 mt-1">
                  <div class="flex justify-between">
                    <dt>{{ config.context.deductible }}</dt>
                    <dd>{{ formatAmount(job.liabilityInfo?.deductible) }}</dd>
                  </div>
                  <div class="flex justify-between">
                    <dt>{{ config.context.remainingLimit }}</dt>
                    <dd>{{ formatAmount(job.liabilityInfo?.remainingLiabilityLimit) }}</dd>
                  </div>
                </dl>
              </ng-container>
              <ng-template #noJob>
                <p class="text-sm italic text-gray-500">{{ config.context.noJob }}</p>
              </ng-template>
            </div>

            <dl class="flex flex-col gap-2 text-sm border-t pt-3" data-testid="context-states">
              <div class="flex justify-between items-center gap-2">
                <dt class="text-gray-500">{{ config.context.claimStatus }}</dt>
                <dd>
                  <vs-badge [theme]="claimTheme(claim.status)">
                    {{ claim.status.replace('CANCELLED', 'CANCELED') | titlecase }}
                  </vs-badge>
                </dd>
              </div>
              <div class="flex justify-between items-center gap-2">
                <dt class="text-gray-500">{{ config.context.authorization }}</dt>
                <dd data-testid="context-authorization" class="text-gray-900 text-right">
                  {{ claim.authorizationState ? config.context.authorizationLabels[claim.authorizationState] : '-' }}
                </dd>
              </div>
              <div class="flex justify-between items-start gap-2">
                <dt class="text-gray-500">{{ config.context.payment }}</dt>
                <dd data-testid="context-payment" class="text-gray-900 text-right">
                  <ng-container *ngIf="paymentReadiness$ | async as readiness">
                    {{ config.context.paymentLabels[readiness] }}
                  </ng-container>
                  <p *ngIf="paymentTotals(claim) as totals" class="text-xs text-gray-500">
                    {{ config.context.paid(formatAmount(totals.paid), formatAmount(totals.authorized)) }}
                  </p>
                </dd>
              </div>
              <div class="flex justify-between items-center gap-2">
                <dt class="text-gray-500">{{ config.context.repairDeadline }}</dt>
                <dd
                  data-testid="context-repair-deadline"
                  class="text-right"
                  [class]="isRepairOverdue(claim) ? 'text-danger-600 font-medium' : 'text-gray-900'"
                >
                  {{ claim.repairDeadline ? (claim.repairDeadline | date: 'mediumDate' : 'UTC') :
                  config.context.noRepairDeadline }}
                </dd>
              </div>

              <!-- Not intake, so editable after authorization -->
              <div class="flex flex-col gap-1" data-testid="context-contact">
                <dt class="text-gray-500">{{ config.context.contactReason }}</dt>
                <dd *vsLet="permissions$ | async as permissions">
                  <div *ngIf="permissions?.editClaim; else contactReadonly" class="flex gap-2 items-center">
                    <vs-select
                      vsInput
                      class="flex-1 !h-9"
                      data-testid="contact-reason-select"
                      [value]="pendingContactReason !== undefined ? pendingContactReason : claim.contactReason"
                      [config]="{ placeholder: config.context.noContact }"
                      (valueChanges)="pickContactReason($any($event), claim)"
                    >
                      <li vs-select-option [value]="null">{{ config.context.noContact }}</li>
                      <li *ngFor="let reason of contactReasonValues" vs-select-option [value]="reason">
                        {{ config.context.contactReasonLabels[reason] }}
                      </li>
                    </vs-select>
                    <button
                      *ngIf="pendingContactReason !== undefined && pendingContactReason !== claim.contactReason"
                      vs-button
                      size="sm"
                      theme="primary"
                      data-testid="contact-reason-save"
                      [disabled]="savingContactReason"
                      (click)="saveContactReason(claim)"
                    >
                      {{ config.context.saveContactReason }}
                    </button>
                  </div>
                  <ng-template #contactReadonly>
                    <p class="text-gray-900">
                      {{ claim.contactReason ? config.context.contactReasonLabels[claim.contactReason] :
                      config.context.noContact }}
                    </p>
                  </ng-template>
                </dd>
              </div>
            </dl>
          </vs-card>
        </aside>

        <main class="flex flex-col gap-4 min-w-0">
          <!-- Money, each value on its own -->
          <vs-card class="p-4" data-testid="workspace-money">
            <div class="flex items-center justify-between gap-2 mb-3">
              <p class="text-sm font-semibold text-gray-900">
                {{ config.money.title }}
                <span *ngIf="claim.totals" class="text-gray-400 font-normal">· {{ claim.totals.currency }}</span>
              </p>
              <span *ngIf="refreshing" class="text-xs text-gray-500" data-testid="money-refreshing">
                {{ config.money.refreshing }}
              </span>
            </div>

            <div
              *ngIf="refreshFailed"
              class="flex items-center justify-between gap-2 rounded-lg border border-danger-200 bg-danger-50 px-3 py-2 mb-3"
              data-testid="money-failed"
            >
              <p class="text-sm text-danger-700">{{ config.money.refreshFailed }}</p>
              <button vs-button size="sm" appearance="outline" (click)="refresh()">{{ config.money.retry }}</button>
            </div>

            <p
              *ngIf="hasOpenDialog"
              class="text-xs text-warn-700 rounded-lg border border-warn-200 bg-warn-50 px-3 py-2 mb-3"
              data-testid="money-unsaved"
            >
              {{ config.money.unsaved }}
            </p>

            <dl
              *ngIf="claim.totals as totals; else noCharges"
              class="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm"
              [class.opacity-60]="refreshing"
            >
              <div>
                <dt class="text-xs text-gray-500">{{ config.money.requested }}</dt>
                <dd data-testid="money-requested">{{ formatAmount(totals.requestedTotal) }}</dd>
              </div>
              <div>
                <dt class="text-xs text-gray-500">{{ config.money.authorizedGross }}</dt>
                <dd data-testid="money-authorized">{{ formatAmount(totals.authorizedGross) }}</dd>
              </div>
              <div>
                <dt class="text-xs text-gray-500">{{ config.money.tax }}</dt>
                <dd data-testid="money-tax">{{ formatAmount(totals.tax) }}</dd>
              </div>
              <div>
                <dt class="text-xs text-gray-500">{{ config.money.deductible }}</dt>
                <dd data-testid="money-deductible">-{{ formatAmount(totals.deductible) }}</dd>
              </div>
              <div class="font-semibold text-gray-900">
                <dt class="text-xs font-normal text-gray-500">{{ config.money.netPayable }}</dt>
                <dd data-testid="money-net">{{ formatAmount(totals.netPayable) }}</dd>
              </div>
            </dl>
            <ng-template #noCharges>
              <p class="text-sm italic text-gray-500">{{ config.money.noCharges }}</p>
            </ng-template>
          </vs-card>

          <!-- Intake before the jobs, like the classic page -->
          <ws-claim-intake-card
            *vsLet="permissions$ | async as permissions"
            [claim]="claim"
            [canEdit]="!!permissions?.editClaim"
            (saved)="refresh()"
          ></ws-claim-intake-card>

          <div class="flex flex-col gap-4">
            <!-- Picking a job only swaps the detail below -->
            <vs-card class="p-2" data-testid="workspace-jobs">
              <div class="px-2 pt-2 pb-1">
                <p class="text-sm font-semibold text-gray-900">{{ config.jobs.title }}</p>
                <p class="text-xs text-gray-500">{{ config.jobs.subtitle }}</p>
              </div>

              <ng-container *ngIf="jobs$ | async as jobs">
                <!-- Radio dot, coloured edge and "Viewing" mark the selected job -->
                <div
                  *vsLet="selectedJob$ | async as selected"
                  role="radiogroup"
                  class="grid gap-2 grid-cols-[repeat(auto-fill,minmax(240px,1fr))] p-1"
                  [attr.aria-label]="config.jobs.title"
                >
                  <button
                    *ngFor="let job of jobs"
                    type="button"
                    role="radio"
                    class="relative w-full text-left rounded-lg pl-3 pr-3 py-2 border cursor-pointer transition-colors flex gap-2"
                    [class]="
                      job.id === selected?.id
                        ? 'border-themed-400 bg-themed-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    "
                    [attr.aria-checked]="job.id === selected?.id"
                    [attr.data-testid]="'job-' + job.id"
                    (click)="selectJob(job.id)"
                  >
                    <span
                      *ngIf="job.id === selected?.id"
                      class="absolute left-0 top-2 bottom-2 w-1 rounded-r bg-themed-500"
                      aria-hidden="true"
                    ></span>

                    <span
                      class="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border"
                      [class]="job.id === selected?.id ? 'border-themed-600' : 'border-gray-300'"
                      aria-hidden="true"
                    >
                      <span *ngIf="job.id === selected?.id" class="h-2 w-2 rounded-full bg-themed-600"></span>
                    </span>

                    <span class="flex-1 min-w-0">
                      <span class="flex items-center justify-between gap-2">
                        <span class="text-sm font-medium text-gray-900 truncate">
                          {{ job.coverage?.title ?? config.jobs.other }}
                        </span>
                        <vs-badge [theme]="jobTheme(job)" [config]="{ displayStatusIcon: true }">
                          {{ job.status | titlecase }}
                        </vs-badge>
                      </span>

                      <span class="flex items-end justify-between gap-2 mt-0.5">
                        <span *ngIf="jobTotals(claim, job.id) as totals" class="text-xs text-gray-500">
                          {{ config.jobs.requested }} {{ formatAmount(totals.requestedTotal) }} · {{
                          config.jobs.authorized }} {{ formatAmount(totals.authorizedGross) }}
                        </span>
                        <span
                          *ngIf="job.id === selected?.id"
                          class="text-xs font-medium text-themed-700 whitespace-nowrap"
                          data-testid="job-viewing"
                        >
                          {{ config.jobs.viewing }}
                        </span>
                      </span>
                    </span>
                  </button>
                </div>

                <p *ngIf="!jobs.length" class="text-sm italic text-gray-500 px-2 py-3" data-testid="jobs-empty">
                  {{ config.jobs.empty }}
                </p>
              </ng-container>
            </vs-card>

            <!-- Selected job -->
            <div class="flex flex-col gap-4 min-w-0" data-testid="workspace-job">
              <ng-container *ngIf="selectedJob$ | async as job; else selectPrompt">
                <ng-container *vsLet="permissions$ | async as permissions">
                  <vs-card class="p-4">
                    <div class="flex items-start justify-between gap-3 flex-wrap">
                      <div class="min-w-0">
                        <div class="flex items-center gap-2">
                          <p class="text-base font-semibold text-gray-900 truncate">
                            {{ job.coverage?.title ?? config.jobs.other }}
                          </p>
                          <vs-badge [theme]="jobTheme(job)" [config]="{ displayStatusIcon: true }">
                            {{ job.status | titlecase }}
                          </vs-badge>
                        </div>
                        <p class="text-xs text-gray-500 mt-0.5">
                          {{ config.job.decision }}: {{ job.adjudicationReason ?? config.job.noDecision }}
                        </p>
                      </div>

                      <!-- Actions follow server state; the API checks again -->
                      <div class="flex gap-2 flex-wrap" *ngIf="actions$ | async as actions" data-testid="job-actions">
                        <ng-container *ngFor="let action of jobActionOrder">
                          <span
                            *ngIf="actions[action].visible"
                            tooltipStyle="light"
                            [vsTooltip]="actions[action].reason ?? ''"
                          >
                            <button
                              vs-button
                              size="sm"
                              [appearance]="action === 'decide' ? 'default' : 'outline'"
                              [theme]="action === 'decide' ? 'primary' : 'default'"
                              [disabled]="!actions[action].enabled"
                              [attr.data-testid]="'action-' + action"
                              (click)="openAction(job, action)"
                            >
                              {{ config.actions[action] }}
                            </button>
                          </span>
                        </ng-container>
                      </div>
                    </div>
                  </vs-card>

                  <!-- White cards, like the dialogs on the classic page -->
                  <div class="rounded-xl bg-base shadow">
                    <ws-claim-item-lines-table
                      #linesTable
                      [claim]="claim"
                      [item]="job"
                      [canManage]="!!permissions?.manageCoverage && claim.status === open && !job.authorizationId && job.status === draft && !job.deleted"
                      [canAdjust]="!!permissions?.adjust && claim.status === open && !job.authorizationId && job.status === draft && !job.deleted"
                      [canViewHistory]="true"
                      (linesChanged)="refresh()"
                    ></ws-claim-item-lines-table>
                  </div>

                  <ws-claim-item-holds
                    class="block rounded-xl bg-base shadow"
                    [claim]="claim"
                    [item]="job"
                    [lines]="(linesTable.lines$ | async) ?? []"
                    [canManage]="!!permissions?.manageHolds && claim.status === open && !job.authorizationId && job.status !== resolved && !job.deleted"
                    [canClose]="!!permissions?.manageHolds && claim.status === open && !job.authorizationId && !job.deleted"
                    [canViewFiles]="!!permissions?.viewFiles"
                    [canUploadFiles]="!!permissions?.uploadFiles"
                    [canAttachEvidence]="!!permissions?.attachEvidence && claim.status === open && !job.authorizationId && !job.deleted"
                    (changed)="refresh()"
                  ></ws-claim-item-holds>

                  <ws-claim-item-eligibility
                    *ngIf="claim.warranty?.policy?.checkClaimEligibility"
                    class="block rounded-xl bg-base shadow"
                    [claim]="claim"
                    [item]="job"
                    [canCheck]="!!permissions?.adjudicate && claim.status === open && !job.deleted"
                  ></ws-claim-item-eligibility>
                </ng-container>
              </ng-container>

              <ng-template #selectPrompt>
                <vs-card class="p-6">
                  <p class="text-sm text-gray-500">{{ config.job.selectPrompt }}</p>
                </vs-card>
              </ng-template>
            </div>
          </div>

          <ng-container *vsLet="permissions$ | async as permissions">
            <ws-claim-authorization-card
              [claim]="claim"
              [canAuthorize]="!!permissions?.authorize"
              [canReverse]="!!permissions?.reverse"
              [isZeroPayAllowed]="(isZeroPayAllowed$ | async) ?? false"
              (changed)="refresh()"
            ></ws-claim-authorization-card>

            <!-- Same approval and split cards as the payments page -->
            <vs-card *ngIf="paymentHolds$ | async as holds" data-testid="workspace-approvals">
              <vs-card-header
                [config]="{ title: config.approvals.title, subtitle: config.approvals.subtitle }"
              ></vs-card-header>
              <div class="flex flex-col gap-4 px-6 pb-6">
                <p
                  *ngIf="holds.reason"
                  data-testid="workspace-held-back"
                  class="rounded-xl border border-warn-200 bg-warn-50 px-4 py-3 text-sm text-warn-800"
                >
                  {{ holds.reason }}
                </p>
                <ws-claim-overrides
                  [claim]="claim"
                  [overrides]="holds.overrides"
                  [paidTargetIds]="holds.paidTargetIds"
                  [canManage]="!!permissions?.manageOverrides"
                  (changed)="refresh()"
                ></ws-claim-overrides>
                <!-- One split per authorization: the first one and its supplementals. -->
                <ws-claim-allocations
                  *ngFor="let balance of claim.paymentBalances"
                  [claim]="claim"
                  [authorizationId]="balance.authorizationId"
                  [canManage]="!!permissions?.manageAllocations"
                  [hold]="holds.byAuthorization[balance.authorizationId]?.override ?? null"
                  (changed)="refresh()"
                ></ws-claim-allocations>
              </div>
            </vs-card>
          </ng-container>

          <!-- The full audit trail, collapsed until it's needed -->
          <vs-card *ngIf="(permissions$ | async)?.viewAudit" class="p-4" data-testid="workspace-audit-card">
            <div class="flex items-start justify-between gap-3 flex-wrap">
              <div class="min-w-0">
                <div class="flex items-center gap-2">
                  <p class="text-base font-semibold text-gray-900">{{ config.audit.title }}</p>
                  <vs-badge *ngIf="auditCount$ | async as count" theme="default" data-testid="audit-count">
                    {{ config.audit.entries(count) }}
                  </vs-badge>
                </div>
                <p class="text-xs text-gray-500 mt-0.5">{{ config.audit.subtitle }}</p>
              </div>

              <button
                vs-button
                size="sm"
                appearance="outline"
                data-testid="toggle-audit"
                [attr.aria-expanded]="showAudit"
                (click)="showAudit = !showAudit"
              >
                {{ showAudit ? config.audit.hide : config.audit.show }}
                <vs-icon [name]="showAudit ? 'chevron-up' : 'chevron-down'"></vs-icon>
              </button>
            </div>

            <div *ngIf="showAudit" class="mt-4 pt-4 border-t" data-testid="workspace-audit">
              <ws-claim-audit [embedded]="true"></ws-claim-audit>
            </div>
          </vs-card>

          <!-- Same attachments card as the classic page -->
          <ws-claim-attachments
            *ngIf="(permissions$ | async)?.viewFiles"
            data-testid="workspace-attachments"
          ></ws-claim-attachments>

          <ws-claim-timeline [claim]="claim" [collapsible]="false"></ws-claim-timeline>
        </main>
      </div>

      <ws-claim-close-dialog #closeDialog></ws-claim-close-dialog>
    </vs-app-page>
  </ng-container>
</ng-container>

<ng-template #otherStates>
  <div class="p-6" [attr.data-testid]="'workspace-' + loadState">
    <vs-card class="p-6 flex flex-col gap-3 items-start">
      <p *ngIf="loadState === 'loading'" class="text-sm text-gray-500">{{ config.states.loading }}</p>
      <ng-container *ngIf="loadState === 'failed'">
        <p class="text-sm text-danger-700">{{ config.states.loadFailed }}</p>
        <a vs-button appearance="outline" routerLink="/claims">{{ config.breadcrumbs.claims }}</a>
      </ng-container>
      <ng-container *ngIf="loadState === 'forbidden'">
        <p class="text-sm text-gray-700">{{ config.states.forbidden }}</p>
        <a vs-button appearance="outline" routerLink="/claims">{{ config.breadcrumbs.claims }}</a>
      </ng-container>
    </vs-card>
  </div>
</ng-template>

<router-outlet></router-outlet>
